import { el, setClass } from './dom';
import { icon } from './Icons';
import { PhotoGallery } from './PhotoGallery';
import { EnhancePanel } from './EnhancePanel';
import { ApiKeyStore } from '@/capture/ApiKeyStore';
import { CaptureUnsupportedError } from '@/capture/CapturePasses';
import type { CaptureService } from '@/capture/CaptureService';
import { PhotoStore } from '@/capture/PhotoStore';
import type { PhotoStorage, StoredShot } from '@/capture/PhotoStorage';
import type { ExportQuality } from '@shared/plans';
import { SIZE_PRESETS, fitCaptureSize, type ImageSize } from '@/capture/ImageSpec';
import type { SemanticTag } from '@/semantics/SemanticTag';

/**
 * Photo mode: a clean, HUD-free view for composing and taking shots.
 *
 * Entering hides the editor entirely — toolbar, palette, inspector, grid, north arrow, selection
 * halos. What remains is the frame, a letterbox showing exactly which pixels the capture will
 * contain (the output aspect rarely matches the browser window), optional composition guides, and a
 * shutter.
 *
 * The overlay owns no domain state. Captures go through {@link CaptureService}, storage through
 * {@link PhotoStore}, and recall pushes camera and environment back through the managers that own
 * them, so a recalled shot behaves exactly as if the user had navigated there by hand.
 */

export interface PhotoModeDeps {
  container: HTMLElement;
  capture: CaptureService;
  /** Hide/show the editor HUD and any on-canvas readout. */
  setEditorChromeVisible: (visible: boolean) => void;
  /** Restore a shot's environment (date, time, weather) alongside its camera. */
  onRecall: (shot: StoredShot) => void;
  onExit: () => void;
  /**
   * Where shots are kept. Defaults to this device's IndexedDB.
   *
   * Supplied by the app as the account-backed store when a user is signed in — photo mode, the
   * gallery, and the enhance panel are all written against the interface and never learn which.
   */
  store?: PhotoStorage;
  /** The credential the AI features run on. Defaults to a browser-held key. */
  apiKeys?: ApiKeyStore;
  /** The site's semantic tags, for the reference photographs attached to them. */
  tags?: () => readonly SemanticTag[];
  /**
   * The plan's export ceiling, passed through to the two places a PNG leaves the app.
   *
   * Read per call rather than captured, so signing in or changing plan mid-session moves the
   * download without rebuilding photo mode. Null — the default — is no ceiling; see
   * `ExportQuality` for why an account is what a ceiling belongs to.
   */
  exportQuality?: () => ExportQuality | null;
}

/** `'viewport'` follows the window; the rest name a documented output size. */
type AspectId = string;

export class PhotoMode {
  readonly root: HTMLElement;
  readonly store: PhotoStorage;
  readonly apiKeys: ApiKeyStore;

  private active = false;
  private aspect: AspectId = 'viewport';
  private guides = true;
  private busy = false;
  private shotCounter = 0;

  private readonly frame: HTMLElement;
  private readonly guideLayer: HTMLElement;
  private readonly readout: HTMLElement;
  private readonly shutter: HTMLButtonElement;
  private readonly flash: HTMLElement;
  private readonly toast: HTMLElement;
  private readonly aspectRow: HTMLElement;
  private readonly gallery: PhotoGallery;
  private readonly enhance: EnhancePanel;
  private readonly onResize = (): void => this.layoutFrame();

  constructor(private readonly deps: PhotoModeDeps) {
    this.store = deps.store ?? new PhotoStore();
    this.apiKeys = deps.apiKeys ?? new ApiKeyStore();
    this.guideLayer = el('div', { class: 'lg-photo__guides' });
    this.frame = el('div', { class: 'lg-photo__frame' }, this.guideLayer);
    this.readout = el('span', { class: 'lg-photo__readout' });
    this.flash = el('div', { class: 'lg-photo__flash' });
    this.toast = el('div', { class: 'lg-photo__toast' });
    this.aspectRow = el('div', { class: 'lg-seg' });

    this.shutter = el('button', {
      class: 'lg-photo__shutter',
      html: icon('shutter', 30),
      title: 'Take a shot',
      attrs: { type: 'button', 'aria-label': 'Take a shot' },
      on: { click: () => void this.takeShot() },
    });

    this.gallery = new PhotoGallery({
      store: this.store,
      onRecall: (shot) => {
        this.deps.onRecall(shot);
        this.showToast(`Recalled “${shot.title}”`);
      },
      onEnhance: (shot) => this.openEnhance(shot),
      onClose: () => this.toggleGallery(false),
      exportQuality: () => this.deps.exportQuality?.() ?? null,
    });

    this.enhance = new EnhancePanel({
      apiKeys: this.apiKeys,
      onSaveVariant: async (shot, variant) => {
        await this.store.addVariant(shot.id, variant);
        await this.gallery.refresh();
      },
      onClose: () => this.closeEnhance(),
      exportQuality: () => this.deps.exportQuality?.() ?? null,
      ...(this.deps.tags ? { tags: this.deps.tags } : {}),
    });

    this.buildAspectRow();

    this.root = el(
      'div',
      { class: 'lg-photo', attrs: { 'aria-label': 'Photo mode' } },
      this.frame,
      this.flash,
      el(
        'div',
        { class: 'lg-photo__bar' },
        el(
          'div',
          { class: 'lg-photo__group' },
          el('span', { class: 'lg-photo__brand', html: icon('camera', 16) }),
          this.readout,
        ),
        el(
          'div',
          { class: 'lg-photo__group' },
          this.aspectRow,
          el('button', {
            class: 'lg-photo__btn',
            text: 'Guides',
            attrs: { type: 'button', 'data-toggle': 'guides' },
            title: 'Rule-of-thirds guides',
            on: {
              click: () => {
                this.guides = !this.guides;
                this.refresh();
              },
            },
          }),
        ),
        this.shutter,
        el(
          'div',
          { class: 'lg-photo__group lg-photo__group--end' },
          el('button', {
            class: 'lg-photo__btn',
            html: `${icon('gallery', 15)}<span>Shots</span>`,
            attrs: { type: 'button', 'data-toggle': 'gallery' },
            title: 'Saved shots',
            on: { click: () => this.toggleGallery() },
          }),
          el('button', {
            class: 'lg-photo__btn lg-photo__btn--exit',
            html: `${icon('close', 15)}<span>Exit</span>`,
            attrs: { type: 'button' },
            title: 'Leave photo mode  ·  C',
            on: { click: () => this.deps.onExit() },
          }),
        ),
      ),
      this.gallery.root,
      this.enhance.root,
      this.toast,
    );

    this.buildGuides();
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Enter photo mode: hide the editor and show the capture frame. */
  enter(): void {
    if (this.active) return;
    this.active = true;
    this.deps.container.appendChild(this.root);
    this.deps.setEditorChromeVisible(false);
    window.addEventListener('resize', this.onResize);
    void this.gallery.refresh();
    this.refresh();
    // Layout needs the element in the document to measure against.
    requestAnimationFrame(() => this.layoutFrame());
  }

  /**
   * Enter photo mode with the saved-shots drawer already open.
   *
   * No longer where the toolbar's Shots button lands — that now opens the full sheet over the
   * designer (`ShotsPanel.ts`), because wanting to look through the shots is not the same errand as
   * wanting to compose a frame and only one of them wants the editor hidden. Kept because arriving
   * in the viewfinder *with* the drawer out is still a coherent request: it is what "take another
   * one like that" looks like.
   */
  openShots(): void {
    this.enter();
    this.toggleGallery(true);
  }

  /** Leave photo mode and restore the editor exactly as it was. */
  exit(): void {
    if (!this.active) return;
    this.active = false;
    window.removeEventListener('resize', this.onResize);
    this.root.remove();
    this.deps.setEditorChromeVisible(true);
  }

  private buildAspectRow(): void {
    const options: { id: AspectId; label: string; title: string }[] = [
      { id: 'viewport', label: 'Fit', title: 'Match the window’s aspect ratio' },
      ...SIZE_PRESETS.map((preset) => ({
        id: preset.id,
        label: preset.label.split('·')[0]?.trim() ?? preset.id,
        title: preset.label,
      })),
    ];

    for (const option of options) {
      this.aspectRow.appendChild(
        el('button', {
          class: 'lg-seg__btn',
          text: option.label,
          title: option.title,
          attrs: { type: 'button', 'data-aspect': option.id },
          on: {
            click: () => {
              this.aspect = option.id;
              this.refresh();
              this.layoutFrame();
            },
          },
        }),
      );
    }
  }

  private buildGuides(): void {
    for (const position of ['33.333%', '66.667%']) {
      this.guideLayer.appendChild(
        el('span', { class: 'lg-photo__guide lg-photo__guide--v', style: { left: position } }),
      );
      this.guideLayer.appendChild(
        el('span', { class: 'lg-photo__guide lg-photo__guide--h', style: { top: position } }),
      );
    }
  }

  /** The capture size the shutter will use. */
  captureSize(): ImageSize {
    if (this.aspect === 'viewport') return this.deps.capture.suggestedSize();
    const preset = SIZE_PRESETS.find((entry) => entry.id === this.aspect);
    return preset?.size ?? fitCaptureSize(1536, 1024);
  }

  private refresh(): void {
    for (const button of this.aspectRow.querySelectorAll<HTMLElement>('[data-aspect]')) {
      setClass(button, 'is-active', button.dataset.aspect === this.aspect);
    }
    const guideButton = this.root.querySelector<HTMLElement>('[data-toggle="guides"]');
    if (guideButton) setClass(guideButton, 'is-active', this.guides);
    this.guideLayer.style.opacity = this.guides ? '1' : '0';

    const size = this.captureSize();
    this.readout.textContent = `${size.width} × ${size.height}`;
    this.shutter.disabled = this.busy || !this.deps.capture.supported;
    setClass(this.shutter, 'is-busy', this.busy);
  }

  /**
   * Size the letterbox to the capture's aspect ratio inside the viewport. The frame is what the
   * capture will contain — without it the user composes against the window and gets a different
   * crop back.
   */
  private layoutFrame(): void {
    const size = this.captureSize();
    const bounds = this.deps.container.getBoundingClientRect();
    // Leave room for the control bar so the frame is never hidden behind it.
    const availableWidth = bounds.width * 0.94;
    const availableHeight = (bounds.height - 96) * 0.94;
    const targetAspect = size.width / size.height;

    let width = availableWidth;
    let height = width / targetAspect;
    if (height > availableHeight) {
      height = availableHeight;
      width = height * targetAspect;
    }

    this.frame.style.width = `${Math.round(width)}px`;
    this.frame.style.height = `${Math.round(height)}px`;
  }

  private async takeShot(): Promise<void> {
    if (this.busy) return;
    if (!this.deps.capture.supported) {
      this.showToast('This browser session cannot capture frames.');
      return;
    }

    this.busy = true;
    this.refresh();
    this.playFlash();

    try {
      const result = await this.deps.capture.capture(this.captureSize());
      this.shotCounter += 1;
      const shot = await this.store.save({
        title: `Shot ${this.shotCounter}`,
        size: result.size,
        beauty: result.beauty,
        idMap: result.idMap,
        depth: result.depth,
        thumbnail: result.thumbnail,
        manifest: result.manifest,
        camera: result.camera,
        environment: result.environment,
      });
      await this.gallery.refresh();
      const count = shot.manifest.visiblePlantCount;
      this.showToast(`Saved “${shot.title}” — ${count} plant${count === 1 ? '' : 's'} identified`);
    } catch (error) {
      const message =
        error instanceof CaptureUnsupportedError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'The capture failed.';
      this.showToast(message);
    } finally {
      this.busy = false;
      this.refresh();
    }
  }

  private toggleGallery(force?: boolean): void {
    const open = force ?? !this.root.classList.contains('is-gallery-open');
    setClass(this.root, 'is-gallery-open', open);
    const button = this.root.querySelector<HTMLElement>('[data-toggle="gallery"]');
    if (button) setClass(button, 'is-active', open);
    if (open) void this.gallery.refresh();
  }

  private openEnhance(shot: StoredShot): void {
    this.enhance.setShot(shot);
    setClass(this.root, 'is-enhance-open', true);
  }

  private closeEnhance(): void {
    setClass(this.root, 'is-enhance-open', false);
    this.enhance.setShot(null);
  }

  private playFlash(): void {
    this.flash.classList.remove('is-firing');
    // Force a reflow so the animation restarts on a rapid second shot.
    void this.flash.offsetWidth;
    this.flash.classList.add('is-firing');
  }

  private showToast(message: string): void {
    this.toast.textContent = message;
    this.toast.classList.add('is-visible');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toast.classList.remove('is-visible'), 2600);
  }

  private toastTimer = 0;

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.clearTimeout(this.toastTimer);
    this.gallery.dispose();
    this.enhance.dispose();
    this.store.close();
    this.root.remove();
  }
}
